import React from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { VexuProductShowcase } from '../../components/deck/VexuProductShowcase';
import { readDeckAudience } from '../../data/investorAccess';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';

/** Network path that travels from the telecom layer toward the device. */
const SIGNAL_PATH = 'M 470 742 C 760 742, 930 630, 1136 502';
const ease = [0.22, 0.61, 0.36, 1] as const;
export function CoverSlide() {
  const isMarketing = readDeckAudience() === 'marketing';
  return <SlideStage data-id="element-2214" data-mp="1385tljasgyyk">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2215" data-mp="137npk0hvxgg3">
        {/* Ambient Vexu-green field */}
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 90% 70% at 8% -18%, rgba(0,200,83,0.07) 0%, rgba(250,252,249,0) 60%), radial-gradient(ellipse 55% 60% at 82% 45%, rgba(0,200,83,0.09) 0%, rgba(250,252,249,0) 68%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 55%, #EFF3ED 100%)'
      }} data-id="element-2216" data-mp="za838d5a86tl" />

        {/* Network layer: concentric field + one incoming call path */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1600 900" fill="none" aria-hidden="true" data-id="element-2217" data-mp="2zkwtxlefeq6a">
          {[250, 350, 452, 560].map((r, i) => <motion.circle key={r} cx={1298} cy={454} r={r} stroke="#00C853" strokeWidth={1} strokeOpacity={0.16 - i * 0.028} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1.6,
          delay: 1.5 + i * 0.12,
          ease
        }} data-id="element-2218" data-mp="2puam4dcyb3y5" data-mpi={i} />)}
          <motion.path d={SIGNAL_PATH} stroke="url(#signalFade)" strokeWidth={1.25} strokeLinecap="round" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 1.8,
          delay: 1.9,
          ease
        }} data-id="element-2219" data-mp="26xsvjz9u9a3h" />
          <defs data-id="element-2220" data-mp="7q2d22aqy6ah">
            <linearGradient id="signalFade" x1="470" y1="742" x2="1136" y2="502" gradientUnits="userSpaceOnUse" data-id="element-2221" data-mp="7pkj5cbk445q">
              <stop stopColor="#009940" stopOpacity="0" data-id="element-2222" data-mp="3pa4lb9p85f9" />
              <stop offset="0.55" stopColor="#009940" stopOpacity="0.45" data-id="element-2223" data-mp="3mnwnaiwbnwi" />
              <stop offset="1" stopColor="#009940" stopOpacity="0.85" data-id="element-2224" data-mp="10dyj3xm7hajx" />
            </linearGradient>
          </defs>
        </svg>

        {/* Signal travelling toward Vexu */}
        <motion.span className="absolute top-0 left-0 w-[7px] h-[7px] rounded-full bg-vexu" style={{
        offsetPath: `path("${SIGNAL_PATH}")`,
        boxShadow: '0 0 14px 3px rgba(0,200,83,0.35)'
      }} initial={{
        offsetDistance: '0%',
        opacity: 0
      }} animate={{
        offsetDistance: '100%',
        opacity: [0, 1, 1, 0]
      }} transition={{
        duration: 3.4,
        delay: 2.6,
        repeat: Infinity,
        repeatDelay: 1.6,
        ease: 'easeInOut'
      }} data-id="element-2225" data-mp="rd098yp368tf" />

        {/* Device — the real Vexu application */}
        <motion.div className="absolute" style={{
        left: 1158,
        top: 150
      }} initial={{
        opacity: 0,
        y: 26
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        delay: 1.35,
        ease
      }} data-id="element-2226" data-mp="1vma7eqxyonzs">
          <VexuProductShowcase scale={0.72} data-id="element-2227" data-mp="2acraeeq8vlys" />
        </motion.div>

        {/* Logo */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 92
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.15,
        ease
      }} data-id="element-2228" data-mp="2aekd5ixqlm1i">
          <img src={VEXU_LOGO} alt="" className="w-9 h-9 object-contain" data-id="element-2229" data-mp="kze2kfixbfsx" />
          <span className="text-[21px] font-bold tracking-tight text-[#0B1712]" data-id="element-2230" data-mp="p0fyonhjnbqo">
            Vexu.ai
          </span>
        </motion.div>

        {/* Headline block */}
        <div className="absolute" style={{
        left: 112,
        top: 262,
        width: 830
      }} data-id="element-2231" data-mp="3ltalzp0xdexn">
          <h1 className="text-[62px] font-semibold leading-[1.12] tracking-[-0.02em] text-[#0B1712]" data-id="element-2232" data-mp="3lvd9a1ap0tyo">
            {['AI call handling that works', 'even when your phone can’t.'].map((line, i) => <motion.span key={line} className="block" initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.1,
            delay: 0.5 + i * 0.18,
            ease
          }} data-id="element-2233" data-mp="373uyqj0lrrmp" data-mpi={i}>
                  {line}
                </motion.span>)}
          </h1>

          <motion.p className="mt-9 max-w-[620px] text-[20px] leading-relaxed text-[#5A6B60]" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 1.05,
          ease
        }} data-id="element-2234" data-mp="2odn1uinhxxey">
            An always-on, telecom-native AI assistant for your existing phone
            number.
          </motion.p>

          <motion.div className="mt-12 flex items-center gap-4" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1.2,
          delay: 3.1,
          ease
        }} data-id="element-2235" data-mp="13pardaahruna">
            <span className="h-px w-10 bg-vexu-600/70" data-id="element-2236" data-mp="1hn8wrq6u4gqn" />
            <span className="text-[16px] font-medium tracking-[0.02em] text-vexu-700" data-id="element-2237" data-mp="1hnsdj3kr0z33">
              Your number. Your AI. Always available.
            </span>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div className="absolute flex items-end justify-between border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        bottom: 68
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 3.5,
        ease
      }} data-id="element-2238" data-mp="18onfrzh0h9rz">
          <div data-id="element-2239" data-mp="23vek1x42wmo8">
            {isMarketing ? <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-vexu-700/80" data-id="element-2240" data-mp="23u399dc7uenm">
                Patent pending
              </p> : <>
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" data-id="element-2241" data-mp="1u4jou38uucqn">
                  Seed Round
                </p>
                <p className="mt-2 text-[15px] font-medium tracking-[0.01em] text-[#33463B]" data-id="element-2242" data-mp="1q26su6jwtt72">
                  $1.5M · SAFE
                </p>
                <p className="mt-2.5 text-[10.5px] font-semibold uppercase tracking-[0.26em] text-vexu-700/80" data-id="element-2243" data-mp="1g4seqdlglghx">
                  Patent pending
                </p>
              </>}
          </div>
          <div className="text-right" data-id="element-2244" data-mp="176gqujbz8wt6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" data-id="element-2245" data-mp="177i2aowwnlbf">
              Vexu.ai
            </p>
            <p className="mt-2 text-[15px] font-medium tracking-[0.01em] text-[#33463B]" data-id="element-2246" data-mp="31imzbadhkzhs">
              {isMarketing ? 'Company Presentation · 2026' : 'Investor Presentation · 2026'}
            </p>
          </div>
        </motion.div>
      </div>
    </SlideStage>;
}